import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  alpha,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import OrbitLogo from '../../common/OrbitLogo';
import ThemeToggleButton from '../../common/ThemeToggleButton';

// Components
import SearchBar from './SearchBar';
import NotificationMenu from './NotificationMenu';
import MessagesMenu from '../../../features/messages/components/MessagesMenu';
import FeedbackMenu from './FeedbackMenu';
import UserMenu from './UserMenu';

const StyledAppBar = styled(AppBar, {
  // Filter out custom props to prevent them from being passed to DOM
  shouldForwardProp: (prop) => prop !== 'drawerOpen' && prop !== 'drawerWidth',
})(({ theme, drawerOpen, drawerWidth }) => ({
  zIndex: theme.zIndex.appBar,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(drawerOpen && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginRight: theme.spacing(3),
}));


export default function AppBarComponent({
  onMenuClick,
  drawerWidth = 280,
  drawerOpen = false
}) {
  const theme = useTheme();

  return (
    <StyledAppBar
      position="fixed"
      elevation={0}
      drawerOpen={drawerOpen}
      drawerWidth={drawerWidth}
    >
      <Toolbar sx={{ 
        minHeight: '53px !important',
        height: '53px !important'
      }}>
        {/* Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="toggle drawer"
          onClick={onMenuClick}
          sx={{
            mx: 1,
            ...(drawerOpen && { display: { md: 'none' } }),
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo - Only show when drawer is closed on desktop */}
        {!drawerOpen && (
          <LogoContainer sx={{ display: { xs: 'none', md: 'flex' } }}>
            <OrbitLogo height={32} />
          </LogoContainer>
        )}

        <Box display={"flex"} justifyContent={"space-between"} width={"100%"} height={"100%"}>
          {/* Search Bar */}
          <Box sx={{ flexGrow: 1, maxWidth: 560, mx: 2, display: 'flex', alignItems: 'center' }}>
            <SearchBar />
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Theme Changer */}
            <ThemeToggleButton />

            {/* Feedback */}
            <FeedbackMenu />

            {/* Messages */}
            <MessagesMenu />

            {/* Notifications */}
            <NotificationMenu />

            {/* User Menu */}
            <UserMenu />
          </Box>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}